import { create } from 'zustand';
import { apiClient } from '../lib/api';

interface MarketQuote {
  symbol: string;
  price: number;
  change: number;
  changePercent: number;
  volume: number;
}

interface AISignal {
  id: string;
  symbol: string;
  signal: 'BUY' | 'SELL' | 'HOLD';
  confidence: number;
  reasoning: string;
  timestamp: string;
}

interface Portfolio {
  id: string;
  name: string;
  totalValue: number;
  cashBalance: number;
  dailyChange: number;
  dailyChangePercent: number;
}

interface Position {
  symbol: string;
  quantity: number;
  averagePrice: number;
  currentPrice: number;
  totalValue: number;
  unrealizedPnl: number;
  unrealizedPnlPercent: number;
}

interface FinancialState {
  user: any | null;
  portfolio: Portfolio | null;
  positions: Position[];
  marketData: Record<string, MarketQuote>;
  aiSignals: AISignal[];
  loading: boolean;
  error: string | null;
  socket: WebSocket | null;
  allocationData: { name: string; value: number; color: string }[];
  performanceData: { time: string; value: number }[];

  // Derived values for components
  totalValue: number;
  totalPnl: number;
  totalPnlPercent: number;
  balance: number;
  signals: AISignal[];
  isLoading: boolean;

  // Actions
  setUser: (user: any) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  
  refreshDashboard: () => Promise<void>;
  connectWebSocket: () => void;
  placeTrade: (order: { 
    symbol: string; 
    side: string; 
    quantity: number; 
    orderType: string; 
    price?: number 
  }) => Promise<boolean>;
  logout: () => Promise<void>;
}

export const useFinancialStore = create<FinancialState>((set, get) => ({
  user: null,
  portfolio: null,
  positions: [],
  marketData: {},
  aiSignals: [],
  loading: false,
  isLoading: false,
  error: null,
  socket: null,
  
  totalValue: 0,
  totalPnl: 0,
  totalPnlPercent: 0,
  balance: 0,
  signals: [],
  allocationData: [],
  performanceData: [],

  setUser: (user) => set({ user }),
  setLoading: (loading) => set({ loading, isLoading: loading }),
  setError: (error) => set({ error }),

  refreshDashboard: async () => {
    set({ loading: true, isLoading: true, error: null });
    try {
      // Run critical data in parallel; isolate positions so a 404 never blocks the rest
      const [userResp, signalsResp, marketResp, portfoliosResp] = await Promise.all([
        apiClient.getCurrentUser(),
        apiClient.getAISignals(),
        apiClient.getMarketData(),
        apiClient.getPortfolios(),
      ]);

      // Positions: gracefully degraded — 404 returns empty array, not a crash
      let positions: any[] = [];
      try {
        const positionsResp = await apiClient.getPositions();
        if (!positionsResp.error) {
          const data = positionsResp.data as any;
          positions = Array.isArray(data?.positions)
            ? data.positions
            : Array.isArray(data)
            ? data
            : [];
        } else if (positionsResp.status !== 404) {
          // Only log unexpected errors; 404 is expected until backend route is confirmed
          console.warn('[Positions] Endpoint unavailable:', positionsResp.error);
        }
      } catch {
        // Silently degrade — positions are non-critical for market-watch page
      }

      const portfolios = (portfoliosResp.data as any) || [];
      const activePortfolio = portfolios.length > 0 ? portfolios[0] : null;
      const aiSignals = (signalsResp.data as any)?.signals || [];

      set({
        user: userResp.data,
        aiSignals: aiSignals,
        signals: aiSignals,
        marketData: (marketResp.data as any)?.quotes || {},
        portfolio: activePortfolio,
        positions,
        totalValue: activePortfolio?.totalValue || 0,
        totalPnl: activePortfolio?.dailyChange || 0,
        totalPnlPercent: activePortfolio?.dailyChangePercent || 0,
        balance: activePortfolio?.cashBalance || 0,
        allocationData: positions.length > 0 ? [
          { name: 'Equities', value: Math.round(positions.filter(p => !['BTC', 'ETH', 'SOL', 'ADA'].some(s => p.symbol.includes(s))).reduce((acc, p) => acc + p.totalValue, 0) / (activePortfolio?.totalValue || 1) * 100), color: '#00FFDE' },
          { name: 'Crypto', value: Math.round(positions.filter(p => ['BTC', 'ETH', 'SOL', 'ADA'].some(s => p.symbol.includes(s))).reduce((acc, p) => acc + p.totalValue, 0) / (activePortfolio?.totalValue || 1) * 100), color: '#9E24FF' },
          { name: 'Cash', value: Math.round((activePortfolio?.cashBalance || 0) / (activePortfolio?.totalValue || 1) * 100), color: '#64748B' }
        ].filter(a => a.value > 0) : [
          { name: 'Cash', value: 100, color: '#64748B' }
        ],
        performanceData: [
          { time: 'T-24h', value: (activePortfolio?.totalValue || 0) - (activePortfolio?.dailyChange || 0) },
          { time: 'Current', value: activePortfolio?.totalValue || 0 }
        ],
        loading: false,
        isLoading: false
      });
    } catch (err) {
      set({ error: "Institutional Link Interrupted.", loading: false, isLoading: false });
    }
  },


  connectWebSocket: (() => {
    let retryCount = 0;
    const MAX_RETRIES = 10;
    const BASE_DELAY_MS = 1000;
    let retryTimer: ReturnType<typeof setTimeout> | null = null;

    return () => {
      const currentState = get();
      if (currentState.socket) return;

      const WS_URL = import.meta.env.VITE_WS_URL || 'ws://localhost:8000/ws';

      let ws: WebSocket;
      try {
        ws = new WebSocket(WS_URL);
      } catch {
        // Constructor can throw synchronously for invalid URLs
        return;
      }

      ws.onopen = () => {
        retryCount = 0; // Reset backoff on successful connection
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (data.type === 'MARKET_TICK') {
            set(state => ({
              marketData: { ...state.marketData, ...data.payload }
            }));
          } else if (data.type === 'AI_SIGNAL') {
            set(state => ({
              aiSignals: [data.payload, ...state.aiSignals.slice(0, 19)]
            }));
          }
        } catch {
          // Suppress payload parse errors silently
        }
      };

      ws.onerror = () => {
        // Suppress — onclose will fire immediately after and handle retry
      };

      ws.onclose = () => {
        set({ socket: null });
        if (retryCount >= MAX_RETRIES) {
          console.warn(`[WS] Max retries (${MAX_RETRIES}) reached. Telemetry stream paused.`);
          return;
        }
        const delay = Math.min(BASE_DELAY_MS * 2 ** retryCount, 60_000);
        retryCount++;
        if (retryTimer) clearTimeout(retryTimer);
        retryTimer = setTimeout(() => get().connectWebSocket(), delay);
      };

      set({ socket: ws });
    };
  })(),

  placeTrade: async (order) => {
    const { portfolio, refreshDashboard } = get();
    if (!portfolio) {
      set({ error: "No Active Portfolio" });
      return false;
    }

    set({ loading: true });
    try {
      const response = await apiClient.placeTradeOrder({
        ...order,
        order_type: order.orderType,
        portfolio_id: portfolio.id
      });

      if (response.error) {
        set({ error: `OMS Error: ${response.error}`, loading: false });
        return false;
      }

      await refreshDashboard();
      return true;
    } catch (err) {
      set({ error: "Execution Link Failed", loading: false });
      return false;
    }
  },

  logout: async () => {
    await apiClient.logout();
    set({ user: null, portfolio: null, positions: [], socket: null });
  }
}));
